import type { ContentRefKind } from '@gitbook/app-content-refs';
import { canInsertInlineIn, INLINE_MENTION } from '@gitbook/doc-core';
import { TextRange, type Value } from '@gitbook/slate';
import type { InlineProps, MarkProps, Plugin } from '@gitbook/slate-react';

import type { CreateInputContentRefPaletteOptions } from '../../ContentReferences';
import { InlineMention } from './InlineMention';
import { MentionPicker } from './MentionPicker';

const MENTION_CONTEXT_TYPE = 'mentionContext';
const CAPTURE_REGEX = /(^|\s)(@|#)(\w*)$/;

/**
 * Slate plugin to detect user typing "@" or "#".
 */
export const mentionPlugin: Plugin = createMentionPlugin({
    accept: {
        '@': ['anchor', 'page', 'space', 'url', 'file', 'user'],
        '#': ['anchor', 'page', 'space', 'url', 'file', 'user'],
    },
});

export function createMentionPlugin(
    options: Omit<CreateInputContentRefPaletteOptions, 'accept' | 'search'> & {
        accept: {
            [symbol: string]: ContentRefKind[];
        };
    }
): Plugin {
    const { accept, ...restOptions } = options;

    const decorateValue = (value: Value): TextRange[] => {
        const { selection } = value;
        const inputValue = getInput(value);
        const decorations: TextRange[] = [];

        if (TextRange.isTextRange(selection)) {
            if (inputValue !== null) {
                decorations.push(
                    TextRange.create({
                        anchorKey: selection.startKey,
                        anchorOffset: selection.startOffset - inputValue.value.length - 1,
                        focusKey: selection.startKey,
                        focusOffset: selection.startOffset,
                        marks: [
                            {
                                type: MENTION_CONTEXT_TYPE,
                                data: {
                                    value: inputValue.value,
                                    symbol: inputValue.symbol,
                                },
                            },
                        ],
                        isAtomic: true,
                    })
                );
            }
        }

        return decorations;
    };

    const renderNode = (props: InlineProps) => {
        const { node } = props;

        switch (node.type) {
            case INLINE_MENTION:
                return <InlineMention {...props} />;
        }
    };

    const renderMark = (props: MarkProps) => {
        const { mark } = props;

        const acceptForSymbol = options.accept[mark.data.get('symbol')];

        if (!acceptForSymbol) {
            return;
        }

        switch (mark.type) {
            case MENTION_CONTEXT_TYPE:
                return (
                    <MentionPicker
                        {...props}
                        options={{
                            ...restOptions,
                            accept: acceptForSymbol,
                        }}
                    />
                );
        }
    };

    const getInput = (value: Value): { symbol: string; value: string } | null => {
        if (!value.isCollapsed) {
            return null;
        }

        const { document, startText, startBlock, selection } = value;

        if (!TextRange.isTextRange(selection)) {
            return null;
        }

        // In some cases, like if the node that was selected gets deleted,
        // `startText` can be null.
        if (!startText || !startBlock) {
            return null;
        }

        if (!canInsertInlineIn({ document, block: startBlock }, INLINE_MENTION)) {
            return null;
        }

        const { startOffset } = selection;
        const textBefore = startText.text.slice(0, startOffset);
        const result = CAPTURE_REGEX.exec(textBefore);

        if (!result) {
            return null;
        }

        return { symbol: result[2], value: result[3] };
    };

    return {
        renderMark,
        renderNode,
        decorateValue,
    };
}
